/**
 * Ryan O'Dowd
 * 2024-12-07
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import CPassage from '@classes/Passage';
import EmptyScreen from '@components/common/EmptyScreen';
import Filter from './Filter';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import ScreenLoader from '@components/ScreenLoader';
import ScreenWrapper from '@containers/ScreenWrapper';
import Text from '@components/common/Text';
import {
  View,
} from 'react-native';
import WrinkleChip from '@components/WrinkleChip';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import {
  updateUserSettings,
} from '@utils/store/actions';

const SORTS = { // @TODO: abstract with useGetWrinkles
  chronological: 'chronological',
  dateLastLeveledUp: 'dateLastLeveledUp',
  dateOfNextLevelUp: 'dateOfNextLevelUp',
  srsLevel: 'srsLevel',
};

const Wrinkles = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => { // @TODO: this need to be in every component?
    return false;
  });

  const userSettings = hooks.useTypedSelector((state) => state.userSettings);

  const [completedWrinkleGroups, inProgressWrinkleGroups] = hooks.useGetWrinkles(null, userSettings.wrinkles_exclude_waiting, userSettings.wrinkles_exclude_paused, userSettings.wrinkles_sort_by, userSettings.wrinkles_group_by_translation, userSettings.wrinkles_group_adjacent, userSettings.wrinkles_show_in_progress_first);
  const multipleTranslationsWrinkles = userSettings.wrinkles_group_by_translation && (inProgressWrinkleGroups.some((wg) => wg[0].translation_id !== inProgressWrinkleGroups[0][0].translation_id) || completedWrinkleGroups.some((wg) => wg[0].translation_id !== completedWrinkleGroups[0][0].translation_id));

  const translationIdsWithWrinkles = inProgressWrinkleGroups.map((wg) => wg[0].translation_id).concat(completedWrinkleGroups.map((wg) => wg[0].translation_id)); // @TODO: this is going to ignore translations that only have wrinkles waiting for level up if that filter is selected (same with hide paused)
  const translationsWithWrinkles = hooks.useTypedSelector((state) => state.translations.filter((t) => translationIdsWithWrinkles.includes(t.id)), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const allWrinkles = [...inProgressWrinkleGroups, ...completedWrinkleGroups].flat();
  const allWrinkleLevelNumbers = [...new Set(allWrinkles.map((w) => +w.level_number))].sort((a, b) => a - b);

  // @TODO: move into redux/db {
  const [_selectedTranslationIds, setSelectedTranslationIds] = useState(translationIdsWithWrinkles);
  const [_selectedLevelNumbers, setSelectedLevelNumbers] = useState(allWrinkleLevelNumbers);
  // @TODO: move into redux/db }

  const visibleCompletedWrinkleGroups = completedWrinkleGroups.filter((wg) => {
    if (!_selectedTranslationIds.includes(+wg[0].translation_id)) {
      return false;
    } else if (!_selectedLevelNumbers.includes(+wg[0].level_number)) { // @TODO: this is insufficient when "group adjacent verses" is true when not all levels are selected since there could be verses with different levels that don't get filtered by useGetWrinkles
      return false;
    /* @TODO: need to map over individuals...this is easier at useGetWrinkles level
    } else if (!userSettings.wrinkles_exclude_waiting || TODO) {
      return false;
    } else if (!userSettings.wrinkles_exclude_paused || TODO) {
      return false;
    */
    }

    return true;
  });
  const visibleInProgressWrinkleGroups = inProgressWrinkleGroups.filter((wg) => {
    if (!_selectedTranslationIds.includes(+wg[0].translation_id)) {
      return false;
    } else if (!_selectedLevelNumbers.includes(+wg[0].level_number)) {
      return false;
    /* @TODO: need to map over individuals...this is easier at useGetWrinkles level
    } else if (!userSettings.wrinkles_exclude_waiting || TODO) {
      return false;
    } else if (!userSettings.wrinkles_exclude_paused || TODO) {
      return false;
    */
    }

    return true;
  });

  const backIcon = <Header.BackIcon />;

  // @TODO: this component is still super slow to load, and i don't know why...
  // @TODO: this shouldn't work for making the profile load faster, but it does {
  const [_foo, setFoo] = useState(true);
  useEffect(() => {
    setTimeout(() => {
      setFoo(false);
    }, 100);
  }, []);
  if (_foo) {
    return (
      <ScreenWrapper>
        <Header
          title='Wrinkles'
          leftItems={[backIcon]}
        />
        <ScreenLoader />
      </ScreenWrapper>
    );
  }
  // @TODO: this shouldn't work for making the page load faster, but it does }

  const renderEmptyScreen = () => {
    if (allWrinkles.length === 0) {
      return (
        <EmptyScreen.Wrapper>
          <EmptyScreen.Text>
            {i18n('memorizedVersesShowHere')}
          </EmptyScreen.Text>
        </EmptyScreen.Wrapper>
      );
    } else if (visibleInProgressWrinkleGroups.length === 0 && visibleCompletedWrinkleGroups.length === 0) {
      return (
        <EmptyScreen.Wrapper>
          <EmptyScreen.Text>
            {i18n('noWrinklesMatchFilter')}
          </EmptyScreen.Text>
        </EmptyScreen.Wrapper>
      );
    }

    return null;
  };

  return (
    <ScreenWrapper>
      <Header
        title='Wrinkles'
        leftItems={[backIcon]}
      />
      {!!allWrinkles.length &&
        <View style={styles.filters}>
          {/* @TODO: be aware of disabling filters programmatically...if one is available, then you do the lesson, might be impossible to toggle the filter */}
          {translationIdsWithWrinkles.length > 1 &&
            <Filter.Wrapper
              titleIcon='book'
              titleLabel='Translations'
            >
              {/* @TODO: this complicates the ui
              <Filter.Option
                text='All'
                selected={true}// @TODO: only if none of the others are
                onPress={() => {
                  setVisibleTranslationIds(translationIdsWithWrinkles);
                }}
              />
              */}
              {translationsWithWrinkles.map((t) => {
                return (
                  <Filter.Option
                    key={t.id}
                    text={t.abbreviation}
                    selected={_selectedTranslationIds.includes(t.id)}
                    // @TODO: disabled if this translation has no VISIBLE wrinkles?
                    onPress={() => {
                      if (_selectedTranslationIds.includes(t.id)) {
                        setSelectedTranslationIds((curr) => curr.filter((id) => id !== t.id));
                      } else {
                        setSelectedTranslationIds((curr) => [...curr, t.id]);
                      }
                    }}
                  />
                );
              })}
            </Filter.Wrapper>
          }
          <Filter.Wrapper
            titleIcon='stairs'
            titleLabel='Levels'
          >
            {/* @TODO:
            <Filter.Option
              text='All'
              selected={true}// @TODO: only if none of the others are
              onPress={() => {
                // @TODO: implement
              }}
            />
            */}
            {allWrinkleLevelNumbers.map((ln) => {// @TODO: should be 1-9 and 10+ (i.e.g not 11, 12, etc.)
              return (
                <Filter.Option
                  key={ln}
                  text={ln}
                  selected={_selectedLevelNumbers.includes(ln)}
                  onPress={() => {
                    if (_selectedLevelNumbers.includes(ln)) {
                      setSelectedLevelNumbers((curr) => curr.filter((n) => n !== ln));
                    } else {
                      setSelectedLevelNumbers((curr) => [...curr, ln]);
                    }
                  }}
                />
              );
            })}
          </Filter.Wrapper>
          {/* @TODO: custom filter for a single book or a search term */}
          <Filter.Wrapper
            titleIcon='cog'
            titleLabel={i18n('options')}
          >
            <Filter.Option
              text={i18n('hidePaused')}
              selected={userSettings.wrinkles_exclude_paused}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_exclude_paused: +!userSettings.wrinkles_exclude_paused}));
              }}
            />
            <Filter.Option
              text={i18n('hideWaitingForLevelUp')}
              selected={userSettings.wrinkles_exclude_waiting}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_exclude_waiting: +!userSettings.wrinkles_exclude_waiting}));
              }}
            />
            <Filter.Option// @TODO: may be performance reasons that this needs to be true always?
              text={i18n('groupAdjacentVerses')}
              selected={userSettings.wrinkles_group_adjacent}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_group_adjacent: +!userSettings.wrinkles_group_adjacent}));
              }}
            />
            <Filter.Option
              text={i18n('showLevelNumbers')}
              selected={userSettings.wrinkles_show_level_numbers}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_show_level_numbers: +!userSettings.wrinkles_show_level_numbers}));
              }}
            />
            <Filter.Option
              text={i18n('showInProgressFirst')}
              selected={userSettings.wrinkles_show_in_progress_first}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_show_in_progress_first: +!userSettings.wrinkles_show_in_progress_first}));
              }}
            />
            <Filter.Option
              text={i18n('groupByTranslation')}
              selected={userSettings.wrinkles_group_by_translation}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_group_by_translation: +!userSettings.wrinkles_group_by_translation}));
              }}
            />
          </Filter.Wrapper>
          <Filter.Wrapper
            titleIcon='sort'
            titleLabel={i18n('sort')}
          >
            <Filter.Option
              text={i18n('chronological')}
              selected={userSettings.wrinkles_sort_by === SORTS.chronological}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_sort_by: SORTS.chronological}));
              }}
            />
            <Filter.Option
              text={i18n('dateLastLeveledUp')}
              selected={userSettings.wrinkles_sort_by === SORTS.dateLastLeveledUp}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_sort_by: SORTS.dateLastLeveledUp}));
              }}
            />
            <Filter.Option
              text={i18n('dateOfNextLevelUp')}
              selected={userSettings.wrinkles_sort_by === SORTS.dateOfNextLevelUp}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_sort_by: SORTS.dateOfNextLevelUp}));
              }}
            />
            <Filter.Option
              text={i18n('levelNumber')}
              selected={userSettings.wrinkles_sort_by === SORTS.srsLevel}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_sort_by: SORTS.srsLevel}));
              }}
            />
            {/* @TODO:
            <Filter.Option
              text='Verse length'
              selected={userSettings.wrinkles_sort_by === SORTS.verseLength}
              onPress={() => {
                dispatch(updateUserSettings({wrinkles_sort_by: SORTS.verseLength}));
              }}
            />
            */}
          </Filter.Wrapper>
        </View>
      }
      <View style={styles.innerContainer}>
        <View style={styles.countsWrapper}>
          <View style={styles.totalsWrapper}>
            <Text style={styles.totalsText}>{i18n('totals')}:</Text>
            {/* @NOTE: combine two gropus because of the case where in progress and completed are combined */}
            <Text style={styles.wrinkleCountText}>{[...visibleInProgressWrinkleGroups, ...visibleCompletedWrinkleGroups].reduce((curr, wrinkleSequence) => curr + wrinkleSequence.filter((w) => w.level_number < globals.srsLevelWrinkleIsEarnedAt).length, 0)} in progress,</Text>
            <Text style={styles.wrinkleCountText}>{[...visibleInProgressWrinkleGroups, ...visibleCompletedWrinkleGroups].reduce((curr, wrinkleSequence) => curr + wrinkleSequence.filter((w) => w.level_number >= globals.srsLevelWrinkleIsEarnedAt).length, 0)} completed</Text>
          </View>
        </View>
        <FullScreenScrollView
          contentContainerStyle={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {renderEmptyScreen() ||
            (
              <React.Fragment>
                <View style={styles.wrinklesWrapper}>
                  {visibleInProgressWrinkleGroups.map((wrinkleGroup, i) => {
                    return (
                      <React.Fragment key={`${wrinkleGroup[0].translation_id}-${wrinkleGroup[0].verse_id}`}>
                        {!!multipleTranslationsWrinkles && (i === 0 || (visibleInProgressWrinkleGroups[i - 1][0].translation_id !== wrinkleGroup[0].translation_id)) && (
                          <View style={styles.translationDivider}>
                            <Text style={styles.translationDividerText}>{CPassage.getTranslation(wrinkleGroup[0].translation_id).name}</Text>
                          </View>
                        )}
                        <WrinkleChip
                          wrinkles={wrinkleGroup}
                          showLevelNumbers={!!userSettings.wrinkles_show_level_numbers}
                        />
                      </React.Fragment>
                    );
                  })}
                </View>
                <View style={styles.wrinklesWrapper}>
                  {visibleCompletedWrinkleGroups.map((wrinkleGroup, i) => {
                    return (
                      <React.Fragment key={`${wrinkleGroup[0].translation_id}-${wrinkleGroup[0].verse_id}`}>
                        {!!multipleTranslationsWrinkles && (i === 0 || (visibleCompletedWrinkleGroups[i - 1][0].translation_id !== wrinkleGroup[0].translation_id)) && (
                          <View style={styles.translationDivider}>
                            <Text style={styles.translationDividerText}>{CPassage.getTranslation(wrinkleGroup[0].translation_id).name}</Text>
                          </View>
                        )}
                        <WrinkleChip
                          wrinkles={wrinkleGroup}
                          showLevelNumbers={!!userSettings.wrinkles_show_level_numbers}
                        />
                      </React.Fragment>
                    );
                  })}
                </View>
              </React.Fragment>
            )
          }
        </FullScreenScrollView>
      </View>
    </ScreenWrapper>
  );
};

export default Wrinkles;
